import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { submitWaitlist } from "@/lib/data";
import { formatSupabaseError } from "@/lib/supabase";
import { useSettings } from "@/hooks/useSettings";

const SERVICE_OPTIONS = [
  { v: "private-1-1", label: "1:1 Private Reading Tutoring" },
  { v: "small-group", label: "Small Group Reading Tutoring" },
  { v: "virtual-1-1", label: "1:1 Virtual / Remote Tutoring" },
  { v: "not-sure", label: "Not sure yet — help me choose" },
];

const FREQ_OPTIONS = [
  { v: "1x", label: "1× per week (maintain / summer slide)" },
  { v: "2x", label: "2× per week (close significant gaps)" },
  { v: "not-sure", label: "Not sure — recommend a plan" },
];

const REFERRAL_OPTIONS = [
  "Google search", "Friend / family", "Doctor or therapist",
  "School recommendation", "Social media", "Other",
];

const INITIAL = {
  parent_name: "", email: "", phone: "",
  child_age: "", child_grade: "",
  service_interest: "", frequency_interest: "",
  concerns: "", message: "", referral_source: "",
};

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { settings } = useSettings();

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSelect = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email) {
      toast.error("Please share your name and email so Jonna can reach out.");
      return;
    }
    setSubmitting(true);
    try {
      await submitWaitlist(form);
      setDone(true);
      toast.success("You're on the list! Jonna will reach out soon.");
      setForm(INITIAL);
    } catch (err) {
      toast.error(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-cream">
      <section className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-testid="contact-page">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-xs uppercase tracking-[0.18em] text-terracotta font-semibold">Get Started</span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink text-balance">
              Join the Waitlist
            </h1>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Tell Jonna a little about your child. She'll reach out personally within a few business days to talk
              through what's going on and recommend a plan that fits your family.
            </p>
            <div className="mt-10 space-y-4">
              <div className="rounded-2xl bg-white border border-ink/5 p-5 flex gap-3" data-testid="contact-info-email">
                <Mail className="text-terracotta flex-none" size={18} />
                <div>
                  <div className="text-xs text-ink/55 uppercase tracking-wider">Email</div>
                  {settings?.business_email ? (
                    <a href={`mailto:${settings.business_email}`} className="text-ink font-medium link-underline">{settings.business_email}</a>
                  ) : (
                    <div className="text-ink/50 italic text-sm">Coming soon — please use the form for now</div>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-ink/5 p-5 flex gap-3" data-testid="contact-info-phone">
                <Phone className="text-terracotta flex-none" size={18} />
                <div>
                  <div className="text-xs text-ink/55 uppercase tracking-wider">Phone</div>
                  {settings?.business_phone ? (
                    <a href={`tel:${settings.business_phone}`} className="text-ink font-medium link-underline">{settings.business_phone}</a>
                  ) : (
                    <div className="text-ink/50 italic text-sm">Coming soon</div>
                  )}
                </div>
              </div>
              <div className="rounded-2xl bg-white border border-ink/5 p-5 flex gap-3" data-testid="contact-info-area">
                <MapPin className="text-terracotta flex-none" size={18} />
                <div>
                  <div className="text-xs text-ink/55 uppercase tracking-wider">Service area</div>
                  <div className="text-ink font-medium">{settings?.service_area || "Springboro, OH"}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            {done ? (
              <div className="rounded-3xl bg-sage-100 border border-sage/30 p-10 sm:p-14 text-center" data-testid="waitlist-success">
                <div className="mx-auto h-14 w-14 rounded-full bg-sage text-white flex items-center justify-center"><CheckCircle2 size={28} /></div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-ink">You're on the list!</h2>
                <p className="mt-3 text-ink/70 max-w-md mx-auto">
                  Thank you for reaching out. Jonna will follow up personally within a few business days. In the
                  meantime, feel free to read more about her approach.
                </p>
                <Button onClick={() => setDone(false)} variant="outline" className="mt-7 rounded-full border-2 border-ink/80 hover:bg-ink hover:text-white" data-testid="waitlist-success-another">
                  Submit another
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="rounded-3xl bg-white border border-ink/5 shadow-sm p-7 sm:p-10 space-y-5" data-testid="waitlist-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="parent_name">Parent / guardian name *</Label>
                    <Input id="parent_name" required value={form.parent_name} onChange={onChange("parent_name")} placeholder="Your full name" className="rounded-xl h-11" data-testid="form-parent-name" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" required value={form.email} onChange={onChange("email")} placeholder="you@example.com" className="rounded-xl h-11" data-testid="form-email" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Phone</Label>
                    <Input id="phone" value={form.phone} onChange={onChange("phone")} placeholder="(555) 123-4567" className="rounded-xl h-11" data-testid="form-phone" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="child_age">Child's age</Label>
                    <Input id="child_age" value={form.child_age} onChange={onChange("child_age")} placeholder="e.g. 8" className="rounded-xl h-11" data-testid="form-child-age" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="child_grade">Child's grade</Label>
                    <Input id="child_grade" value={form.child_grade} onChange={onChange("child_grade")} placeholder="e.g. 3rd" className="rounded-xl h-11" data-testid="form-child-grade" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <Label>Service interest</Label>
                    <Select value={form.service_interest} onValueChange={onSelect("service_interest")}>
                      <SelectTrigger className="rounded-xl h-11" data-testid="form-service">
                        <SelectValue placeholder="Choose a service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Preferred frequency</Label>
                    <Select value={form.frequency_interest} onValueChange={onSelect("frequency_interest")}>
                      <SelectTrigger className="rounded-xl h-11" data-testid="form-frequency">
                        <SelectValue placeholder="How often?" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQ_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="concerns">What reading concerns do you have? *</Label>
                  <Textarea id="concerns" rows={4} value={form.concerns} onChange={onChange("concerns")} placeholder="Has your child been evaluated for dyslexia or ADHD? What does reading look like for them right now? Anything that's helped or hurt in the past?" className="rounded-xl resize-none" data-testid="form-concerns" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="message">Anything else Jonna should know?</Label>
                  <Textarea id="message" rows={3} value={form.message} onChange={onChange("message")} placeholder="Preferred days/times, sibling needs, school context, questions for Jonna..." className="rounded-xl resize-none" data-testid="form-message" />
                </div>
                <div className="space-y-1.5">
                  <Label>How did you hear about Jonna?</Label>
                  <Select value={form.referral_source} onValueChange={onSelect("referral_source")}>
                    <SelectTrigger className="rounded-xl h-11" data-testid="form-referral">
                      <SelectValue placeholder="Select one" />
                    </SelectTrigger>
                    <SelectContent>
                      {REFERRAL_OPTIONS.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="pt-2">
                  <Button type="submit" disabled={submitting} className="rounded-full bg-terracotta hover:bg-terracotta-600 text-white h-12 px-7 shadow-md hover:-translate-y-0.5 transition-all w-full sm:w-auto" data-testid="waitlist-submit">
                    {submitting ? "Sending..." : (<><Send size={16} className="mr-1.5" /> Join the Waitlist</>)}
                  </Button>
                </div>
                <p className="text-xs text-ink/50">By submitting, you agree to be contacted by Jonna about tutoring. Your info stays private — never shared, never sold.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
